/**
 * Where a Fragebogen 2 invitation stands, derived from the timestamps the
 * candidate's page writes. Kept pure so the dialog, the card and the tests all
 * read the same answer.
 */

/** A started questionnaire untouched for this long counts as stalled. */
export const INACTIVE_AFTER_DAYS = 7;

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

function toTime(value) {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
}

/**
 * state: 'pending' | 'opened' | 'in_progress' | 'inactive' | 'submitted'
 * idleDays is set for 'inactive'.
 */
export function inviteActivity(invite, now = Date.now()) {
  if (toTime(invite?.submittedAt)) return { state: 'submitted' };

  const opened = toTime(invite?.openedAt);
  if (!opened) return { state: 'pending' };

  const lastActive = toTime(invite.lastActiveAt) ?? opened;
  const idleDays = Math.floor((now - lastActive) / DAY_MS);
  if (idleDays >= INACTIVE_AFTER_DAYS) return { state: 'inactive', idleDays };

  return { state: invite.progress > 0 ? 'in_progress' : 'opened' };
}

/** Counts for the summary line. "inWork" is opened or started, not stalled, not sent. */
export function summarizeInvites(invites, now = Date.now()) {
  const summary = { total: 0, opened: 0, inWork: 0, inactive: 0, submitted: 0 };
  for (const invite of invites || []) {
    const { state } = inviteActivity(invite, now);
    summary.total += 1;
    if (state !== 'pending') summary.opened += 1;
    if (state === 'opened' || state === 'in_progress') summary.inWork += 1;
    if (state === 'inactive') summary.inactive += 1;
    if (state === 'submitted') summary.submitted += 1;
  }
  return summary;
}

/** "gerade eben", "vor 5 Min.", "vor 3 Std.", "vor 1 Tag", "vor 9 Tagen". */
export function formatSince(value, now = Date.now()) {
  const time = toTime(value);
  if (time === null) return null;

  const elapsed = Math.max(0, now - time);
  if (elapsed < MINUTE_MS) return 'gerade eben';
  if (elapsed < HOUR_MS) return `vor ${Math.floor(elapsed / MINUTE_MS)} Min.`;
  if (elapsed < DAY_MS) return `vor ${Math.floor(elapsed / HOUR_MS)} Std.`;

  const days = Math.floor(elapsed / DAY_MS);
  return days === 1 ? 'vor 1 Tag' : `vor ${days} Tagen`;
}
